import React, { useMemo } from 'react'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { calculateMonthlyMetrics } from '@/engine/calculateMonthlyMetrics'
import { LOCATION_DATA } from '@/data/locationData'
import { useSimulationData } from '@/hooks/useSimulationData'
import { Check, Users, Pencil, Trash2, Battery, Sun, Zap, Activity, Trophy, Info, Car } from 'lucide-react'
import type { ConfigId } from '@/types'
import { PlanBreakdownModal } from '@/components/modals/PlanBreakdownModal'



export const PlanMatrix: React.FC<{ hideHeader?: boolean; isSorted?: boolean }> = ({ hideHeader, isSorted = false }) => {
  const [breakdownPlan, setBreakdownPlan] = React.useState<any>(null)
  const [isBreakdownOpen, setIsBreakdownOpen] = React.useState(false)
  const [isScrolled, setIsScrolled] = React.useState(false)
  const config = useEnergyStore((s) => s.config)
  const plans = useEnergyStore((s) => s.plans)
  const portfolios = useEnergyStore((s) => s.portfolios)
  const activePortfolioId = useEnergyStore((s) => s.activePortfolioId)
  const selectedPlanName = useEnergyStore((s) => s.selectedPlan)
  const setSelectedPlan = useEnergyStore((s) => s.setSelectedPlan)
  const selectedConfigId = useEnergyStore((s) => s.selectedConfig)
  const setSelectedConfig = useEnergyStore((s) => s.setSelectedConfig)
  const darkMode = useEnergyStore((s) => s.darkMode)
  const activeHouseholdId = useEnergyStore((s) => s.activeHouseholdId)
  const currentUserId = useEnergyStore((s) => s.userId)
  const { data: simulationMonthlyData } = useSimulationData()

  // Ensure we don't stay on a hidden config
  React.useEffect(() => {
    const hasSolar = config.pvSize > 0 && config.useSolar !== false
    const hasEss = config.useEss && config.useSolar !== false
    if (selectedConfigId === 'solar' && !hasSolar) {
      setSelectedConfig(hasEss ? 'solar_bat' : 'raw')
    } else if (selectedConfigId === 'solar_bat' && !hasEss) {
      setSelectedConfig(hasSolar ? 'solar' : 'raw')
    }
  }, [config.pvSize, config.useSolar, config.useEss, selectedConfigId, setSelectedConfig])

  const matrixData = useMemo(() => {
    const locData = LOCATION_DATA[config.location]
    const locMult = config.location === 'Custom' ? (config.customLocationMultiplier ?? 1.0) : (locData?.multiplier ?? 1.0)
    const locPriceMult = locData?.priceMult ?? 1.0

    const filteredPlans = plans.filter(p => {
      // Base access rules
      if (p.userId && p.userId !== currentUserId && (!p.isShared || p.householdId !== activeHouseholdId)) {
        return false
      }
      
      // Portfolio filter
      if (activePortfolioId !== 'all') {
        const portfolio = portfolios.find(port => port.id === activePortfolioId)
        if (portfolio && !portfolio.planIds.includes(p.id)) {
          return false
        }
      }
      
      return true
    })

    return filteredPlans.map((plan) => {
      const calcForConfig = (configId: ConfigId) => {
        let totalCost = 0
        let totalFlat = 0
        let totalEvCost = 0
        let evPer100km = 0
        
        for (let i = 0; i < 16; i++) {
          const simAdjust = simulationMonthlyData.length > 0 ? (simulationMonthlyData[i % 12] ?? 0) : 0
          const metrics = calculateMonthlyMetrics(
            i,
            config,
            plan,
            configId,
            locMult,
            locPriceMult,
            simAdjust
          )
          totalCost += metrics.netCost
          totalFlat += metrics.flatHours
          totalEvCost += metrics.monthlyEvCost
          evPer100km = metrics.evCostPer100km
        }
        return { 
          cost: Math.round(totalCost * (12 / 16)), 
          flat: Math.round(totalFlat * (12 / 16)), 
          evMonthly: totalEvCost / 16, 
          evPer100km 
        }
      }

      return {
        plan,
        solar: calcForConfig('solar'),
        solar_bat: calcForConfig('solar_bat'),
        raw: calcForConfig('raw')
      }
    })
  }, [config, plans, activeHouseholdId, currentUserId, simulationMonthlyData, portfolios, activePortfolioId])

  const sortedMatrixData = useMemo(() => {
    if (!isSorted) return matrixData
    return [...matrixData].sort((a, b) => a[selectedConfigId].cost - b[selectedConfigId].cost)
  }, [matrixData, selectedConfigId, isSorted])

  const configStats = useMemo(() => {
    const stats: Record<ConfigId, { minCost: number; maxCost: number }> = {
      solar: { minCost: Infinity, maxCost: -Infinity },
      solar_bat: { minCost: Infinity, maxCost: -Infinity },
      raw: { minCost: Infinity, maxCost: -Infinity }
    }

    matrixData.forEach(row => {
      ;(['solar', 'solar_bat', 'raw'] as ConfigId[]).forEach(configId => {
        const c = row[configId].cost
        if (c < stats[configId].minCost) stats[configId].minCost = c
        if (c > stats[configId].maxCost) stats[configId].maxCost = c
      })
    })

    return stats
  }, [matrixData])

  const [expandedPlanId, setExpandedPlanId] = React.useState<string | null>(null)

  // Determine the active configuration tab directly from the global state, ensuring we only show valid tabs
  const validConfigs = (['solar', 'solar_bat', 'raw'] as ConfigId[]).filter(c => 
    (c !== 'solar_bat' || (config.useEss && config.useSolar !== false)) && 
    (c !== 'solar' || (config.pvSize > 0 && config.useSolar !== false))
  )

  // Ensure selectedConfigId is valid for the current hardware toggle state
  const activeTab = validConfigs.includes(selectedConfigId) ? selectedConfigId : validConfigs[validConfigs.length - 1]

  // Data prepped specifically for the active tab, sorted by cost (Kanban/List view inherently demands sorting)
  const activeData = useMemo(() => {
    return matrixData.map(row => ({
      plan: row.plan,
      metrics: row[activeTab],
      isActiveScenario: selectedPlanName === row.plan.plan && selectedConfigId === activeTab
    })).sort((a, b) => a.metrics.cost - b.metrics.cost)
  }, [matrixData, activeTab, selectedPlanName, selectedConfigId])

  const minCost = activeData[0]?.metrics.cost || 0
  const maxCost = activeData[activeData.length - 1]?.metrics.cost || 1

  return (
    <div className={`rounded-xl md:rounded-2xl border overflow-hidden transition-colors duration-300 ${darkMode ? 'bg-slate-950 border-white/5' : 'bg-[#f8fbff] border-slate-200'}`}>
      
      {!hideHeader && (
        <div className={`p-4 md:p-6 border-b flex flex-col md:flex-row md:items-center justify-between gap-4 ${darkMode ? 'border-white/5 bg-slate-900/30' : 'border-slate-100 bg-white'}`}>
          <div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => useEnergyStore.getState().openHelpToItem('Plan Comparison')}
                className={`shrink-0 p-1 rounded-full transition-colors cursor-pointer ${darkMode ? 'text-slate-500 hover:text-emerald-400' : 'text-slate-400 hover:text-emerald-600'}`}
              >
                <Info size={14} />
              </button>
              <h2 className="text-lg md:text-xl font-black tracking-tight uppercase">Provider Rankings</h2>
            </div>
            <p className={`text-[10px] md:text-xs font-bold mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Ranked list of retail plans based on your simulated energy profile
            </p>
          </div>
          <button
            onClick={() => useEnergyStore.getState().setPlanModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-emerald-600 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase hover:bg-emerald-700 transition-all shadow-sm active:scale-95 cursor-pointer w-full md:w-auto"
          >
            + Add New Plan
          </button>
        </div>
      )}

      <div className="p-3 md:p-6 flex flex-col gap-6">
        
        {/* MOBILE-FIRST SEGMENTED CONTROL */}
        <div className={`flex p-1.5 rounded-2xl md:rounded-full relative w-full md:w-auto md:min-w-[500px] md:mx-auto shadow-inner ${darkMode ? 'bg-slate-900/80 border border-white/5' : 'bg-slate-100/80 border border-slate-200/50'}`}>
          {validConfigs.map((configId) => {
            const isActive = activeTab === configId
            return (
              <button
                key={configId}
                onClick={() => setSelectedConfig(configId)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl md:rounded-full text-[10px] md:text-xs font-black uppercase tracking-wider transition-all duration-300 relative z-10 cursor-pointer ${
                  isActive 
                    ? (darkMode ? 'text-emerald-400 bg-slate-800 shadow-md ring-1 ring-white/10' : 'text-emerald-700 bg-white shadow-md ring-1 ring-slate-900/5')
                    : (darkMode ? 'text-slate-500 hover:text-slate-300 hover:bg-white/5' : 'text-slate-500 hover:text-slate-700 hover:bg-black/5')
                }`}
              >
                {configId === 'solar' && <Sun size={14} className={isActive ? 'text-amber-500' : ''} />}
                {configId === 'solar_bat' && <Battery size={14} className={isActive ? 'text-emerald-500' : ''} />}
                {configId === 'raw' && <Zap size={14} className={isActive ? 'text-blue-500' : ''} />}
                <span className="hidden sm:inline whitespace-nowrap">
                  {configId === 'solar' && 'Solar Only'}
                  {configId === 'solar_bat' && 'Solar + Storage'}
                  {configId === 'raw' && 'Grid Only'}
                </span>
                <span className="sm:hidden whitespace-nowrap">
                  {configId === 'solar' && 'Solar'}
                  {configId === 'solar_bat' && 'Sol+Bat'}
                  {configId === 'raw' && 'Grid'}
                </span>
              </button>
            )
          })}
        </div>

        {/* RANKED ACCORDION LIST */}
        <div className="flex flex-col gap-3">
          {activeData.map((item, index) => {
            const isExpanded = expandedPlanId === item.plan.id
            const isCheapest = index === 0
            const extraCost = item.metrics.cost - minCost
            
            // Background fill percentage for visual scale (Mobile First trick)
            const costRange = Math.max(1, maxCost - minCost)
            const fillPercent = Math.min(100, Math.max(0, (extraCost / costRange) * 100))

            return (
              <div 
                key={item.plan.id || item.plan.plan}
                className={`relative flex flex-col rounded-2xl md:rounded-3xl border transition-all duration-300 overflow-hidden ${
                  item.isActiveScenario
                    ? (darkMode ? 'border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.1)]' : 'border-emerald-500/40 shadow-xl shadow-emerald-500/5')
                    : isExpanded
                      ? (darkMode ? 'border-slate-600 bg-slate-900/80 shadow-lg' : 'border-slate-300 bg-white shadow-lg')
                      : (darkMode ? 'border-white/5 bg-slate-900/40 hover:bg-slate-800' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm')
                }`}
              >
                {/* Visual Fill Bar (Subtle background indication of relative cost) */}
                {!item.isActiveScenario && (
                  <div 
                    className="absolute top-0 right-0 bottom-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]"
                    style={{ 
                      width: `${fillPercent}%`, 
                      background: 'linear-gradient(to left, currentColor, transparent)',
                      color: isCheapest ? '#10b981' : '#f43f5e'
                    }} 
                  />
                )}

                {/* Collapsed Card Header */}
                <div 
                  className={`px-3 py-3 md:px-5 md:py-5 ${isExpanded ? 'pb-2 md:pb-2' : ''} flex items-start justify-between cursor-pointer relative z-10 select-none group`}
                  onClick={() => setExpandedPlanId(isExpanded ? null : (item.plan.id || item.plan.plan))}
                >
                  <div className="flex flex-col min-w-0 pr-2">
                    <div className="flex items-center gap-3 md:gap-5">
                      {/* Rank Badge */}
                      <div className={`shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-xl md:rounded-2xl flex items-center justify-center font-black shadow-inner transition-colors duration-300 ${
                        isCheapest
                          ? 'bg-gradient-to-br from-amber-300 to-amber-500 text-amber-950 shadow-amber-500/20 text-xs md:text-sm'
                          : item.isActiveScenario
                            ? (darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600')
                            : (darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500')
                      }`}>
                        {isCheapest ? <Trophy size={14} className="md:w-4 md:h-4" /> : `#${index + 1}`}
                      </div>

                      <div className="flex gap-2 items-center min-w-0">
                        {item.isActiveScenario && <Check size={12} className={`text-emerald-500 shrink-0`} />}
                        <h3 className={`font-black text-xs md:text-base transition-colors duration-300 ${!isExpanded ? 'truncate' : ''} ${
                          item.isActiveScenario
                            ? (darkMode ? 'text-emerald-400' : 'text-emerald-600')
                            : (darkMode ? 'text-white group-hover:text-emerald-400' : 'text-slate-900 group-hover:text-emerald-600')
                        }`}>
                          {item.plan.plan}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Rates Preview (Mobile friendly) */}
                    <div 
                      className={`flex items-center gap-1.5 transition-all duration-300 overflow-hidden pl-[44px] md:pl-[60px] ${
                        !isExpanded ? 'max-h-6 mt-1 md:mt-1.5 opacity-60' : 'max-h-0 mt-0 opacity-0'
                      }`}
                    >
                      <span className={`text-[9px] font-bold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        ${item.plan.fixedCharge}/d
                      </span>
                      <span className="text-[8px] opacity-30">•</span>
                      <span className={`text-[9px] font-bold ${darkMode ? 'text-rose-400' : 'text-rose-600'}`}>
                        P:{item.plan.peakRate}¢
                      </span>
                      {(item.plan.shoulderRate > 0 || item.plan.nightRate > 0) && (
                        <>
                          <span className="text-[8px] opacity-30">•</span>
                          <span className={`text-[9px] font-bold ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            N:{item.plan.nightRate}¢
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="text-right shrink-0 pl-2">
                    <div className={`text-base md:text-2xl font-black tracking-tighter ${
                      isCheapest ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-white' : 'text-slate-900')
                    }`}>
                      ${item.metrics.cost.toLocaleString()}
                    </div>
                    <div className={`text-[9px] md:text-[10px] font-bold mt-0.5 md:mt-1 uppercase tracking-wider ${
                      isCheapest 
                        ? (darkMode ? 'text-amber-400' : 'text-amber-600') 
                        : (darkMode ? 'text-rose-400/80' : 'text-rose-500/80')
                    }`}>
                      {isCheapest ? '★ Best Value' : `+$${extraCost.toLocaleString()} /yr`}
                    </div>
                    {config.useEv && (
                      <div className={`flex items-center justify-end gap-1 text-[9px] md:text-[10px] font-bold mt-1 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                        <Car size={10} /> ${item.metrics.evPer100km.toFixed(2)}<span className="opacity-60 text-[8px] md:text-[9px]">/100km</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded Accordion Area (Progressive Disclosure) */}
                <div 
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className={`px-4 pb-4 md:px-6 md:pb-6 pt-3 md:pt-4 border-t flex flex-col gap-4 md:gap-5 ${
                    darkMode ? 'border-white/5 bg-slate-900/60' : 'border-slate-100 bg-slate-50/80'
                  }`}>
                    
                    {/* Detailed Metrics Grid */}
                    <div className="flex flex-wrap gap-3 md:gap-4">
                      <div className={`flex-1 min-w-[120px] p-3 rounded-xl border ${darkMode ? 'bg-slate-950/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                        <div className="text-[9px] font-black uppercase text-slate-500 mb-1">Fixed Daily</div>
                        <div className={`text-sm md:text-base font-black ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>${item.plan.fixedCharge.toFixed(2)}</div>
                      </div>
                      <div className={`flex-1 min-w-[120px] p-3 rounded-xl border ${darkMode ? 'bg-slate-950/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                        <div className="text-[9px] font-black uppercase text-slate-500 mb-1">Peak Rate</div>
                        <div className={`text-sm md:text-base font-black ${darkMode ? 'text-rose-400' : 'text-rose-600'}`}>{item.plan.peakRate.toFixed(2)}¢</div>
                      </div>
                      <div className={`flex-1 min-w-[120px] p-3 rounded-xl border ${darkMode ? 'bg-slate-950/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                        <div className="text-[9px] font-black uppercase text-slate-500 mb-1">Off-Peak / Night</div>
                        <div className={`text-sm md:text-base font-black ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{item.plan.nightRate.toFixed(2)}¢</div>
                      </div>
                      {item.plan.peakFit > 0 && (
                        <div className={`flex-1 min-w-[120px] p-3 rounded-xl border ${darkMode ? 'bg-slate-950/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                          <div className="text-[9px] font-black uppercase text-slate-500 mb-1">Peak Export</div>
                          <div className={`text-sm md:text-base font-black ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{item.plan.peakFit.toFixed(2)}¢</div>
                        </div>
                      )}
                      {(item.plan.shoulderFit || 0) > 0 && (
                        <div className={`flex-1 min-w-[120px] p-3 rounded-xl border ${darkMode ? 'bg-slate-950/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                          <div className="text-[9px] font-black uppercase text-slate-500 mb-1">Shoulder Export</div>
                          <div className={`text-sm md:text-base font-black ${darkMode ? 'text-amber-400/80' : 'text-amber-600/80'}`}>{item.plan.shoulderFit!.toFixed(2)}¢</div>
                        </div>
                      )}
                      {item.plan.offPeakFit > 0 && (
                        <div className={`flex-1 min-w-[120px] p-3 rounded-xl border ${darkMode ? 'bg-slate-950/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                          <div className="text-[9px] font-black uppercase text-slate-500 mb-1">Off-Peak Export</div>
                          <div className={`text-sm md:text-base font-black ${darkMode ? 'text-amber-400/60' : 'text-amber-600/60'}`}>{item.plan.offPeakFit.toFixed(2)}¢</div>
                        </div>
                      )}
                      {config.useEv && (
                        <div className={`flex-1 min-w-[120px] p-3 rounded-xl border ${darkMode ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200 shadow-sm'}`}>
                          <div className={`flex items-center gap-1 text-[9px] font-black uppercase mb-1 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            <Car size={10} /> EV Cost
                          </div>
                          <div className={`text-sm md:text-base font-black ${darkMode ? 'text-indigo-500' : 'text-indigo-700'}`}>
                            ${item.metrics.evPer100km.toFixed(2)} <span className="text-[10px] opacity-60">/100km</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions Row */}
                    <div className="flex flex-col lg:flex-row justify-between gap-4 pt-2">
                      <button
                        onClick={() => {
                          setSelectedPlan(item.plan.plan)
                          setSelectedConfig(activeTab)
                          setExpandedPlanId(null)
                        }}
                        className={`w-full lg:w-auto px-8 flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-black uppercase transition-all shadow-sm active:scale-95 cursor-pointer ${
                          item.isActiveScenario
                            ? (darkMode ? 'bg-slate-800 text-emerald-400 border border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border border-emerald-200')
                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                      >
                        {item.isActiveScenario ? <><Check size={14} /> Active Scenario</> : 'Set as Active Scenario'}
                      </button>

                      <div className="grid grid-cols-2 lg:flex gap-2 w-full lg:w-auto">
                        <button
                          onClick={() => {
                            setBreakdownPlan(item.plan)
                            setIsBreakdownOpen(true)
                          }}
                          className={`col-span-2 lg:col-span-1 lg:flex-none flex items-center justify-center gap-1.5 px-6 py-3 rounded-xl text-[10px] font-black uppercase transition-colors border cursor-pointer ${
                            darkMode ? 'bg-slate-800 border-slate-700 text-blue-400 hover:bg-slate-700' : 'bg-white border-slate-200 text-blue-600 hover:bg-slate-50'
                          }`}
                        >
                          <Activity size={12} /> <span className="inline">Breakdown</span>
                        </button>
                        <button
                          onClick={() => useEnergyStore.getState().setPlanModalOpen(true, item.plan.id)}
                          className={`col-span-1 lg:flex-none flex items-center justify-center gap-1.5 px-6 py-3 rounded-xl text-[10px] font-black uppercase transition-colors border cursor-pointer ${
                            darkMode ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <Pencil size={12} /> <span className="inline">Edit</span>
                        </button>
                        <button
                          onClick={() => {
                            useEnergyStore.getState().openConfirm({
                              title: 'Delete Plan',
                              message: `Are you sure you want to delete the plan "${item.plan.plan}"?`,
                              confirmText: 'Delete',
                              isDestructive: true,
                              onConfirm: () => useEnergyStore.getState().removePlan(item.plan.id!),
                            })
                          }}
                          className={`col-span-1 lg:flex-none flex items-center justify-center gap-1.5 px-6 py-3 rounded-xl text-[10px] font-black uppercase transition-colors border cursor-pointer ${
                            darkMode ? 'bg-rose-500/10 border-rose-500/20 text-rose-400 hover:bg-rose-500/20' : 'bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100'
                          }`}
                        >
                          <Trash2 size={12} /> <span className="inline">Delete</span>
                        </button>
                      </div>
                    </div>
                    
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <PlanBreakdownModal
        isOpen={isBreakdownOpen}
        onClose={() => setIsBreakdownOpen(false)}
        darkMode={darkMode}
        plan={breakdownPlan}
      />
    </div>
  )
}
